import { useMemo, useState } from 'react';
import { ExternalLink, MessageSquareText } from 'lucide-react';
import type { PaneTable, TableRow } from '../../shared/contracts';
import { formatTableCell } from '../../shared/format';

const PAGE_SIZE = 50;
const NUMERIC = new Set(['currency', 'number', 'percent']);

/**
 * Grouping is ADAPTIVE: 2 tickets per category is noise, 100 rows in a few sale types
 * is structure. Group by default only when the rows are many AND the groups meaningful.
 */
export function shouldGroupByDefault(rows: TableRow[]): boolean {
  // Missing groups render as 'Øvrige', so they count as a group here too — otherwise
  // the 12-group ceiling could be exceeded at render time.
  const groups = new Set(rows.map((r) => r.group ?? 'Øvrige'));
  if (groups.size < 2) return false;
  return rows.length >= 12 && rows.length / groups.size >= 3 && groups.size <= 12;
}

function sum(rows: TableRow[], key: string): number {
  return rows.reduce((acc, r) => {
    const v = r.cells[key];
    return acc + (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  }, 0);
}

/** Rich entity table for the drill modal: formatted columns, adaptive grouping, totals.
 *  `hideClosed`/`onHideClosedChange` make the closed-filter controllable by the parent so
 *  it survives the table unmounting (e.g. the thread round trip in the drill modal). */
export function DrillTable({
  table,
  onDrillRow,
  hideClosed: hideClosedProp,
  onHideClosedChange,
}: {
  table: PaneTable;
  onDrillRow?: (row: TableRow) => void;
  hideClosed?: boolean;
  onHideClosedChange?: (hide: boolean) => void;
}) {
  const { columns, rows, totals, groupLabel } = table;
  const groupable = Boolean(groupLabel) && rows.some((r) => r.group != null);
  const [grouped, setGrouped] = useState(() => groupable && shouldGroupByDefault(rows));
  const [limit, setLimit] = useState(PAGE_SIZE);
  // Closed records are VISIBLE by default and can be toggled away — the pane's counts
  // always include them; this only filters the table view.
  const [hideClosedLocal, setHideClosedLocal] = useState(false);
  const hideClosed = hideClosedProp ?? hideClosedLocal;
  const setHideClosed = onHideClosedChange ?? setHideClosedLocal;
  const closedCount = useMemo(() => rows.filter((r) => r.closed).length, [rows]);
  const activeRows = useMemo(() => (hideClosed ? rows.filter((r) => !r.closed) : rows), [hideClosed, rows]);

  const groups = useMemo(() => {
    if (!grouped) return null;
    const byGroup = new Map<string, TableRow[]>();
    for (const r of activeRows) {
      const g = r.group ?? 'Øvrige';
      const list = byGroup.get(g) ?? [];
      list.push(r);
      byGroup.set(g, list);
    }
    return [...byGroup.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [grouped, activeRows]);

  const visible = grouped ? activeRows : activeRows.slice(0, limit);

  const headerCell = (align: boolean, label: string, key: string) => (
    <th key={key} className={`whitespace-nowrap px-2 py-1.5 text-[11px] font-medium uppercase tracking-wide text-brand-dark/45 ${align ? 'text-right' : 'text-left'}`}>
      {label}
    </th>
  );

  const bodyRow = (r: TableRow) => (
    // A background tint (not opacity) marks closed rows — text keeps WCAG AA contrast.
    <tr key={r.id} className={`border-t border-brand-primary/5 hover:bg-brand-primary/[0.03] ${r.closed ? 'bg-brand-dark/[0.04]' : ''}`}>
      {columns.map((c, i) => (
        <td key={c.key} className={`px-2 py-2 text-sm ${NUMERIC.has(c.format ?? 'text') ? 'text-right tabular-nums' : 'text-left'} ${i === 0 ? 'font-medium text-brand-dark' : 'text-brand-dark/70'}`}>
          {formatTableCell(r.cells[c.key] ?? null, c.format)}
        </td>
      ))}
      <td className="w-8 px-2 py-2 text-right">
        {r.drill && onDrillRow ? (
          <button type="button" onClick={() => onDrillRow(r)} className="text-brand-accent hover:text-brand-accent-hover" title="Vis korrespondance">
            <MessageSquareText size={15} />
          </button>
        ) : r.href ? (
          <a href={r.href} target="_blank" rel="noreferrer" className="text-brand-accent hover:text-brand-accent-hover" title="Åbn i kildesystem">
            <ExternalLink size={15} />
          </a>
        ) : null}
      </td>
    </tr>
  );

  const totalsRow = (scope: TableRow[], label: string) =>
    totals?.length ? (
      <tr className="border-t-2 border-brand-primary/15 bg-brand-primary/[0.03]">
        {columns.map((c, i) => (
          <td key={c.key} className={`px-2 py-2 text-sm font-semibold ${NUMERIC.has(c.format ?? 'text') ? 'text-right tabular-nums text-brand-primary' : 'text-left text-brand-dark/60'}`}>
            {totals.includes(c.key) ? formatTableCell(sum(scope, c.key), c.format) : i === 0 ? label : ''}
          </td>
        ))}
        <td />
      </tr>
    ) : null;

  return (
    <div>
      {(groupable || closedCount > 0) && (
        <div className="mb-2 flex justify-end gap-2">
          {closedCount > 0 && (
            <button
              type="button"
              onClick={() => setHideClosed(!hideClosed)}
              className="rounded-full bg-brand-primary/5 px-2.5 py-1 text-[11px] font-medium text-brand-dark/60 hover:bg-brand-primary/10"
            >
              {hideClosed ? `Vis lukkede (${closedCount})` : `Skjul lukkede (${closedCount})`}
            </button>
          )}
          {groupable && (
            <button
              type="button"
              onClick={() => setGrouped((g) => !g)}
              className="rounded-full bg-brand-primary/5 px-2.5 py-1 text-[11px] font-medium text-brand-dark/60 hover:bg-brand-primary/10"
            >
              {grouped ? 'Vis samlet' : `Gruppér efter ${groupLabel?.toLowerCase()}`}
            </button>
          )}
        </div>
      )}
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {columns.map((c) => headerCell(NUMERIC.has(c.format ?? 'text'), c.label, c.key))}
            <th className="w-8" />
          </tr>
        </thead>
        {grouped && groups ? (
          groups.map(([g, groupRows]) => (
            <tbody key={g}>
              <tr>
                <td colSpan={columns.length + 1} className="bg-brand-primary/[0.05] px-2 py-1.5 text-xs font-semibold text-brand-dark/70">
                  {g} <span className="font-normal text-brand-dark/45">({groupRows.length})</span>
                </td>
              </tr>
              {groupRows.map(bodyRow)}
              {totalsRow(groupRows, 'Subtotal')}
            </tbody>
          ))
        ) : (
          <tbody>{visible.map(bodyRow)}</tbody>
        )}
        {(!grouped || !groups) && <tbody>{totalsRow(activeRows, 'Total')}</tbody>}
        {grouped && groups && groups.length > 1 && <tbody>{totalsRow(activeRows, 'Total')}</tbody>}
      </table>
      {!grouped && activeRows.length > visible.length && (
        <div className="mt-2 flex items-center justify-between border-t border-brand-primary/10 pt-2">
          <span className="text-xs text-brand-dark/50">Viser {visible.length} af {activeRows.length} rækker</span>
          <button type="button" onClick={() => setLimit((l) => l + PAGE_SIZE)} className="rounded px-2 py-1 text-xs font-medium text-brand-accent hover:bg-brand-primary/5 hover:text-brand-accent-hover">
            Vis flere
          </button>
        </div>
      )}
    </div>
  );
}
